import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Save as SaveIcon, Restore as RestoreIcon, Settings as SettingsIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import ConfirmDialog from '../../ConfirmDialog';
import api from '../../../services/api';

export default function SettingsTab() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [showResetDialog, setShowResetDialog] = useState(false);

  // Fetch sync status
  const {
    data: syncStatusResponse,
    isLoading: syncStatusLoading
  } = useQuery({
    queryKey: ['customerSyncSettingsSyncStatus'],
    queryFn: async () => {
      const response = await api.get('/customer-sync/status');
      return response.data;
    },
    refetchOnWindowFocus: true,
    refetchInterval: 5000, // Poll every 5 seconds
    staleTime: 0
  });

  const isSyncRunning = syncStatusResponse?.data?.isRunning || false;

  // State for settings form
  const [settings, setSettings] = useState({
    query_batch_size: '',
    default_password: '',
    whole_customers_query: '',
    updated_customers_query: '',
    customer_columns: '',
    api_endpoint: '',
    customer_tags: '',
    deletion_metafield: {
      namespace: '',
      key: ''
    },
    country_code_mapping: {
      mappings: {}
    }
  });

  const [mappingValidationError, setMappingValidationError] = useState('');
  // const [showDefaultPassword, setShowDefaultPassword] = useState(false);

  // Validate country code mappings JSON
  const validateCountryCodeMappings = (mappingsText) => {
    // Handle both string and object inputs
    let mappingsString;
    if (typeof mappingsText === 'object' && mappingsText !== null) {
      mappingsString = JSON.stringify(mappingsText);
    } else if (typeof mappingsText === 'string') {
      mappingsString = mappingsText;
    } else if (!mappingsText) {
      return { isValid: true, error: '' };
    } else {
      return { isValid: false, error: 'Invalid mappings format' };
    }

    if (!mappingsString || mappingsString.trim() === '' || mappingsString.trim() === '{}') {
      return { isValid: true, error: '' };
    }

    try {
      const mappings = JSON.parse(mappingsString);

      if (typeof mappings !== 'object' || mappings === null) {
        return { isValid: false, error: 'Mappings must be a JSON object' };
      }

      // Check if all values are strings
      for (const [key, value] of Object.entries(mappings)) {
        if (typeof key !== 'string' || typeof value !== 'string') {
          return { isValid: false, error: 'All keys and values must be strings' };
        }
        if (value.length !== 2) {
          return { isValid: false, error: 'All values must be 2-digit ISO country codes' };
        }
      }

      return { isValid: true, error: '' };
    } catch (error) {
      return { isValid: false, error: 'Invalid JSON format' };
    }
  };

  // Fetch settings
  const {
    data: settingsData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['customerSyncSettings', 'erp-to-shopify'],
    queryFn: async () => {
      const response = await api.getCustomerSyncSettings();
      return response.data.data;
    },
    staleTime: 0
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (updatedSettings) => {
      const response = await api.updateCustomerSyncSettings(updatedSettings);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Customer sync settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['customerSyncSettings', 'erp-to-shopify'] });
    },
    onError: (error) => {
      if (error.response?.status === 423) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to update settings: ${error.response?.data?.message || error.message}`);
      }
    }
  });

  // Reset settings mutation
  const resetSettingsMutation = useMutation({
    mutationFn: async () => {
      const response = await api.resetCustomerSyncSettings();
      return response.data;
    },
    onSuccess: () => {
      toast.success('Customer sync settings reset to default successfully');
      queryClient.invalidateQueries({ queryKey: ['customerSyncSettings', 'erp-to-shopify'] });
      setShowResetDialog(false);
    },
    onError: (error) => {
      if (error.response?.status === 423) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to reset settings: ${error.response?.data?.message || error.message}`);
      }
      setShowResetDialog(false);
    }
  });

  // Initialize form state when data is loaded
  useEffect(() => {
    if (settingsData?.erpToShopifyCustomerSyncSettings) {
      const erpToShopifyCustomerSyncSettings = settingsData.erpToShopifyCustomerSyncSettings;

      // Format column array as string if it's an array
      let columnsStr = erpToShopifyCustomerSyncSettings.customer_columns;
      if (Array.isArray(columnsStr)) {
        columnsStr = columnsStr.join(', ');
      }

      setSettings({
        query_batch_size: erpToShopifyCustomerSyncSettings.query_batch_size,
        default_password: erpToShopifyCustomerSyncSettings.default_password || '',
        whole_customers_query: erpToShopifyCustomerSyncSettings.whole_customers_query,
        updated_customers_query: erpToShopifyCustomerSyncSettings.updated_customers_query,
        customer_columns: columnsStr,
        api_endpoint: erpToShopifyCustomerSyncSettings.api_endpoint,
        customer_tags: erpToShopifyCustomerSyncSettings.customer_tags,
        deletion_metafield: erpToShopifyCustomerSyncSettings.deletion_metafield || {
          namespace: '',
          key: ''
        },
        country_code_mapping: erpToShopifyCustomerSyncSettings.country_code_mapping || {
          mappings: {}
        }
      });
    }
  }, [settingsData]);

  // Handle form input changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;

    if (name.startsWith('deletion_metafield.')) {
      const field = name.split('.')[1];
      setSettings(prev => ({
        ...prev,
        deletion_metafield: {
          ...prev.deletion_metafield,
          [field]: value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();

    // Validate default password
    if (!settings.default_password || settings.default_password.trim() === '') {
      toast.error('Default password is required');
      return;
    }
    if (settings.default_password.length > 20) {
      toast.error('Default password must not exceed 20 characters');
      return;
    }

    // Validate country code mappings before submission
    const validation = validateCountryCodeMappings(settings.country_code_mapping.mappings);
    if (!validation.isValid) {
      toast.error(`Country code mappings validation failed: ${validation.error}`);
      return;
    }

    // Format columns string as array
    let columnsArray = settings.customer_columns;
    if (typeof columnsArray === 'string' && columnsArray.trim() !== '') {
      columnsArray = columnsArray.split(',').map(col => col.trim());
    }

    // Parse mappings from string to object if not empty
    let parsedMappings = settings.country_code_mapping.mappings;
    if (typeof parsedMappings === 'string' && parsedMappings.trim() !== '') {
      try {
        parsedMappings = JSON.parse(parsedMappings);
      } catch (e) {
        toast.error('Invalid JSON in country code mappings');
        return;
      }
    }

    const updatedSettings = {
      ...settings,
      query_batch_size: parseInt(settings.query_batch_size, 10),
      customer_columns: columnsArray,
      customer_tags: settings.customer_tags?.trim(),
      deletion_metafield: settings.deletion_metafield,
      country_code_mapping: {
        ...settings.country_code_mapping,
        mappings: parsedMappings
      }
    };

    updateSettingsMutation.mutate(updatedSettings);
  };

  // Handle reset settings
  const handleResetSettings = () => {
    setShowResetDialog(true);
  };

  const handleConfirmReset = () => {
    resetSettingsMutation.mutate();
  };

  if (isLoading || syncStatusLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading settings: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Sync in progress warning banner */}
      {isSyncRunning && isAdmin && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Customer synchronization is currently in progress. Settings updates are disabled until the sync completes.
        </Alert>
      )}

      <Paper sx={{ p: '15px' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Box display="flex" alignItems="center">
            <SettingsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              ERP to Shopify Customer Sync Settings
            </Typography>
          </Box>
          {isAdmin ? (
            <>
              <Button
                variant="contained"
                color="warning"
                onClick={handleResetSettings}
                disabled={!isAdmin || resetSettingsMutation.isPending || isSyncRunning}
                startIcon={<RestoreIcon />}
              >
                Reset
              </Button>
            </>
          ) : (<></>)}
        </Box>

        {!settingsData?.erpToShopifyCustomerSyncSettings ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="API Endpoint"
                  name="api_endpoint"
                  value={settings.api_endpoint}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText="The endpoint used for reading data from ERP"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Query Batch Size"
                  name="query_batch_size"
                  type="number"
                  value={settings.query_batch_size}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText="Number of customers to process in each batch"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">items</InputAdornment>
                  }}
                  required
                />
              </Grid>

              {/* <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Default Password"
                  name="default_password"
                  type={showDefaultPassword ? 'text' : 'password'}
                  value={settings.default_password}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText={settings.default_password && settings.default_password.length > 20
                    ? 'Default password must not exceed 20 characters'
                    : 'Default password to set for new customers created in Shopify'}
                  variant="outlined"
                  sx={{ mb: 2 }}
                  autoComplete="new-password"
                  required
                  inputProps={{
                    autoComplete: 'new-password',
                    maxLength: 20
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowDefaultPassword(!showDefaultPassword)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          disabled={!isAdmin || isSyncRunning}
                        >
                          {showDefaultPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid> */}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Whole Customers Query"
                  name="whole_customers_query"
                  value={settings.whole_customers_query}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText="Query used to fetch all customers from ERP. Use ${company} as a placeholder for the company ID."
                  variant="outlined"
                  sx={{ mb: 2 }}
                  multiline
                  rows={4}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Updated Customers Query"
                  name="updated_customers_query"
                  value={settings.updated_customers_query}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText="Query used to fetch today's updated customers from ERP. Use ${company} as a placeholder for the company ID."
                  variant="outlined"
                  sx={{ mb: 2 }}
                  multiline
                  rows={4}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Columns"
                  name="customer_columns"
                  value={settings.customer_columns}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText="Comma-separated list of columns to fetch from ERP"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  multiline
                  rows={3}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Tags"
                  name="customer_tags"
                  value={settings.customer_tags}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText="Comma-separated list of ERP fields to use as customer tags in Shopify"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  multiline
                  rows={2}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                  Customer Deletion Metafield Configuration
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Metafield Namespace"
                  name="deletion_metafield.namespace"
                  value={settings.deletion_metafield.namespace}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText="Namespace for the customer deletion metafield"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Metafield Key"
                  name="deletion_metafield.key"
                  value={settings.deletion_metafield.key}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText="Key for the customer deletion metafield"
                  variant="outlined"
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                  Country Code Mapping Configuration
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure country code mapping to convert country codes from into 2-digit ISO codes for Shopify.
                  This is required because Shopify only accepts 2-digit ISO country codes.
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Country Code Mappings"
                  name="country_code_mapping.mappings"
                  value={typeof settings.country_code_mapping.mappings === 'string'
                    ? settings.country_code_mapping.mappings
                    : JSON.stringify(settings.country_code_mapping.mappings, null, 2)
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    setSettings(prev => ({
                      ...prev,
                      country_code_mapping: {
                        ...prev.country_code_mapping,
                        mappings: value
                      }
                    }));

                    // Validate the mappings
                    const validation = validateCountryCodeMappings(value);
                    setMappingValidationError(validation.error);
                  }}
                  disabled={!isAdmin || isSyncRunning}
                  helperText={mappingValidationError || 'Enter country code mappings in JSON format'}
                  error={!!mappingValidationError}
                  variant="outlined"
                  sx={{ mb: 2 }}
                  multiline
                  rows={20}
                  InputProps={{
                    style: {
                      fontFamily: 'monospace',
                      fontSize: '12px'
                    }
                  }}
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              {isAdmin ? (
                <>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={updateSettingsMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={!isAdmin || updateSettingsMutation.isPending || isSyncRunning || !!mappingValidationError || !settings.default_password || settings.default_password.trim() === '' || settings.default_password.length > 20}
                  >
                    {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                  </Button>
                </>
              ) : (<></>)}
            </Box>
          </form>
        )}
      </Paper>

      <ConfirmDialog
        open={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={handleConfirmReset}
        confirmColor="warning"
        title="Reset Settings to Default"
        message="Are you sure you want to reset all ERP to Shopify customer sync settings to their default values? This action cannot be undone."
        confirmText="Reset"
        cancelText="Cancel"
        severity="warning"
        isLoading={resetSettingsMutation.isPending}
      />
    </Box>
  );
}
